import { Injectable } from '@angular/core';
import { AlertInputOptions, ShowAlertNotificationOptions, ShowConfirmAlertOptions } from '../interfaces/alerts.interface';
import sweetalert, { SweetAlertResult } from 'sweetalert2';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AlertsService {

  constructor(
    private router: Router,
  ) { }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retona una alerta de confirmacion. 
    * @param title Titulo de la alerta.
    * @param html Texto de la alerta.
    * @param icon Icono de la alerta.
    * @param confirmButtonText Texto del boton de confirmacion.
    * @param cancelButtonText Texto del boton de cancelar.
    * @param showDenyButton Boton adicional de cerrado de alerta.
    * @param denyButtonText Texto para el boton adicional.
    * @param allowEscapeKey Bloqueo para el cierre de la alerta con la tecla escape.
    * @param allowOutsideClick Cierre de la alerta si se da click por fuera de ella.
  */
  showConfirmAlert({ title = '¿Está seguro?', html, icon = 'question', confirmButtonText = 'Si', cancelButtonText = 'No', showDenyButton = false, denyButtonText = 'Cerrar', allowEscapeKey = true, allowOutsideClick = true }: ShowConfirmAlertOptions): Promise<SweetAlertResult> {
    return sweetalert.fire({
      title,
      html,
      icon,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        container: 'swal2-container'
      },
      confirmButtonColor: '#4b3c8c',
      denyButtonColor: '#0ea5e9',
      showDenyButton,
      denyButtonText,
      allowEscapeKey,
      allowOutsideClick
    });
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retona una alerta de confirmacion. 
    * @param title Titulo de la alerta.
    * @param html Texto de la alerta.
    * @param icon Icono de la alerta.
    * @param confirmButtonText Texto del boton de confirmacion.
    * @param cancelButtonText Texto del boton de cancelar.
    * @param showDenyButton Boton adicional de cerrado de alerta.
    * @param denyButtonText Texto para el boton adicional.
    * @param allowEscapeKey Bloqueo para el cierre de la alerta con la tecla escape.
    * @param allowOutsideClick Cierre de la alerta si se da click por fuera de ella.
    * @param onCloseRedirect Redirecciona a la ruta cuando es eliminado el modal del inspector del navegador.
  */
  showConfirmAlertTwo({
    title = '¿Está seguro?',
    html,
    icon = 'question',
    confirmButtonText = 'Si',
    cancelButtonText = 'No',
    showDenyButton = false,
    denyButtonText = 'Cerrar',
    allowEscapeKey = true,
    allowOutsideClick = true,
    onCloseRedirect
  }: ShowConfirmAlertOptions & { onCloseRedirect?: string }): Promise<SweetAlertResult> {
    if (onCloseRedirect) {
      // Creamos un observer para vigilar el DOM
      const observer = new MutationObserver((mutations) => {
        const modal = document.querySelector('.swal2-container');
        if (!modal) {
          // 🚀 si desapareció del DOM a la fuerza → redirigir
          this.closeAllAlerts();
          this.router.navigate([onCloseRedirect]);
          observer.disconnect(); // detener observador
        }
      });

      // Empezamos a vigilar cambios en el body
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return sweetalert.fire({
      title,
      html,
      icon,
      showCancelButton: false,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        container: 'swal2-container'
      },
      confirmButtonColor: '#4b3c8c',
      denyButtonColor: '#0ea5e9',
      showDenyButton,
      denyButtonText,
      allowEscapeKey,
      allowOutsideClick,
    });
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-10-14
    * Metodo que retona una alerta basica de notificacion. 
    * @param title Titulo de la alerta.
    * @param html Texto de la alerta.
    * @param icon Icono de la alerta.
    * @param confirmButtonText Texto del boton de confirmacion.
    * @param showConfirmButton Se muestra o no un boton de cerrado.
    * @param timer Tiempo de cierre de la notificacion.
  */
  showAlertNotification({ title = '¡Excelente!', html, icon = 'success', confirmButtonText = 'Cerrar', showConfirmButton = false, timer = 3000 }: ShowAlertNotificationOptions): void {
    const timerValue = timer === 0 ? undefined : timer;
    sweetalert.fire({
      title,
      html,
      icon,
      confirmButtonText,
      showConfirmButton,
      timer: timerValue,
      customClass: {
        container: 'swal2-container'
      },
      confirmButtonColor: '#4b3c8c'
    });
  }
  /**
    * @author Ministerio de cultura
    * @createdate 2024-11-03
    * Metodo que retona el valor registrado en el formulario. 
    * @param title Titulo de la alerta.
    * @param input Tipo de campo.
    * @param inputOptions Opciones si el campo es tipo select.
    * @param inputPlaceholder Titulo de la alerta.
    * @param icon Icono de la alerta.
  */
  async showAlertWithInputForm({ title, input, inputOptions = {}, inputPlaceholder, icon = 'question', messageError = 'Por favor seleccione una opción' }: AlertInputOptions): Promise<{ value: string | null, confirmed: boolean }> {
    const { value, isConfirmed } = await sweetalert.fire({
      title,
      input,
      inputOptions,
      inputPlaceholder,
      showCancelButton: true,
      icon,
      inputValidator: (value: string) => {
        return new Promise<string | null>((resolve) => {
          if (value) resolve(null);
          else resolve(messageError);
        });
      }
    });
    return {
      value: value || null,
      confirmed: isConfirmed
    };
  }

  closeAllAlerts(): void {
    sweetalert.close();
  }
}